import { useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { refreshAfterSave } from "./refreshAfterSave";

/** Navigation state a page passes when it sends the user back to a listing after changing a record. */
export const REFRESH_LIST_STATE = { refreshList: true } as const;

/**
 * Keeps a listing current after a record changes somewhere the listing can't see (an editor or a detail page).
 * The listing's own fetch on arrival reads the search index before the change is projected into it, so it
 * would show the old rows until a manual reload. When the listing is opened with REFRESH_LIST_STATE this
 * re-reads it across the projection window (see refreshAfterSave); the returned `refreshSoon` does the same
 * after a change made on the listing itself (delete, activate, deactivate).
 */
export function useListRefresh(refetch: () => void): () => void {
  const location = useLocation();
  const navigate = useNavigate();
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;
  const cancels = useRef<(() => void)[]>([]);

  const refreshSoon = useCallback(() => {
    cancels.current.push(refreshAfterSave(() => refetchRef.current()));
  }, []);

  // Pending re-reads stop when the listing goes away, not when the location state is cleared below.
  useEffect(() => () => cancels.current.forEach((cancel) => cancel()), []);

  useEffect(() => {
    if ((location.state as { refreshList?: boolean } | null)?.refreshList) {
      refreshSoon();
      // Drop the marker so a reload or Back doesn't re-trigger it.
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return refreshSoon;
}
